import type { VehicleType } from "@daytrip/legacy-enums";
import { Currency, OrderStatus, OrderType, PaymentMethod } from "@daytrip/legacy-enums";
import { isUndefinedOrNull } from "@daytrip/utils";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import {
    ArrayNotEmpty,
    IsArray,
    IsDate,
    IsDefined,
    IsInt,
    IsString,
    IsUUID,
    IsEnum,
    IsBoolean,
    IsOptional,
} from "class-validator";

import { AnonymizedPassenger } from "./AnonymizedPassenger";
import { OrderContentLocation } from "./OrderContentLocation";
import { OrderCustomLocation } from "./OrderCustomLocation";
import { VehicleTypePriceFee } from "./VehicleTypePriceFee";

@Exclude()
export class AnonymizedOrderForDriver {
    @IsOptional()
    @IsString()
    @IsUUID()
    @Expose()
    public _id?: string;

    @IsDate()
    @IsDefined()
    @Expose()
    @Type(() => Date)
    public departureAt: Date;

    @IsUUID("4")
    @IsDefined()
    @Expose()
    public originLocationId: string;

    @IsUUID("4")
    @IsDefined()
    @Expose()
    public destinationLocationId: string;

    @IsUUID("4")
    @IsDefined()
    @Expose()
    public pricingCountryId?: string;

    // TODO @MaxLength
    @IsString()
    @IsDefined()
    @Expose()
    public pickupAddress: string;

    // TODO @MaxLength
    @IsString()
    @IsDefined()
    @Expose()
    public dropoffAddress: string;

    @ArrayNotEmpty()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => AnonymizedPassenger)
    public passengers: Array<AnonymizedPassenger>;

    @ArrayNotEmpty()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Number)
    public vehicles: Array<VehicleType>;

    @IsInt()
    @IsDefined()
    @Expose()
    public tollPrice: number = 0;

    @IsInt()
    @IsDefined()
    @Expose()
    public tollFee: number = 0;

    @IsInt()
    @IsDefined()
    @Expose()
    public additionalPrice: number = 0;

    @IsInt()
    @IsDefined()
    @Expose()
    public additionalFee: number = 0;

    @IsEnum(Currency)
    @IsDefined()
    @Expose()
    public pricingCurrency: Currency = Currency.Euro;

    @Expose()
    @Transform((value) => (value == null ? undefined : value))
    public discountIds: Array<string> = [];

    @IsArray()
    @Expose()
    @Type(() => VehicleTypePriceFee)
    public vehicleTypesPricesFees: Array<VehicleTypePriceFee> = [];

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => OrderContentLocation)
    public contentLocations: Array<OrderContentLocation> = [];

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => OrderCustomLocation)
    public customLocations: Array<OrderCustomLocation> = [];

    @IsDate()
    @Expose()
    @Transform((value) => (isUndefinedOrNull(value) ? null : new Date(value)))
    public acceptedAt: Date | undefined = undefined;

    @IsDate()
    @Expose()
    @Transform((value) => (isUndefinedOrNull(value) ? null : new Date(value)))
    public declinedAt: Date | undefined = undefined;

    @IsDate()
    @Expose()
    @Transform((value) => (isUndefinedOrNull(value) ? null : new Date(value)))
    public confirmedAt: Date | undefined = undefined;

    @IsDate()
    @Expose()
    @Transform((value) => (isUndefinedOrNull(value) ? null : new Date(value)))
    public cancelledAt: Date | undefined = undefined;

    @IsString()
    @IsDefined()
    @Expose()
    public driverNote: string = "";

    @IsString()
    @IsDefined()
    @Expose()
    public customerNote: string = "";

    @IsInt()
    @IsDefined()
    @Expose()
    public paymentMethod: PaymentMethod = PaymentMethod.Online;

    @IsBoolean()
    @Expose()
    public potentialFraud?: boolean = false;

    @IsInt()
    @IsDefined()
    @Expose()
    public type: OrderType = OrderType.Private;

    public get status(): OrderStatus {
        if (!isUndefinedOrNull(this.cancelledAt)) {
            return OrderStatus.Cancelled;
        }

        if (!isUndefinedOrNull(this.declinedAt)) {
            return OrderStatus.Declined;
        }

        if (!isUndefinedOrNull(this.acceptedAt)) {
            if (!isUndefinedOrNull(this.confirmedAt)) {
                return OrderStatus.Confirmed;
            }

            return OrderStatus.Accepted;
        }

        return OrderStatus.Pending;
    }
}
